import { computed, type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { AbstractControl, ValidationErrors } from '@angular/forms';

import { of, startWith, switchMap } from 'rxjs';

/** A reactive snapshot of the parts of an `AbstractControl` a form UI needs to render. */
export interface ControlState {
  invalid: boolean;
  touched: boolean;
  dirty: boolean;
  pending: boolean;
  errors: ValidationErrors | null;
  /** Whether validation feedback should be shown now (invalid and the user has interacted). */
  showError: boolean;
}

const EMPTY_STATE: ControlState = {
  invalid: false,
  touched: false,
  dirty: false,
  pending: false,
  errors: null,
  showError: false,
};

/**
 * Turns a signal of an `AbstractControl` into a signal of its {@link ControlState}, recomputed on
 * every control event (value, status, touched or pristine change). This is the single reactive
 * primitive every form control in the design system uses to drive its error visuals, so all of
 * them react identically to whatever validators the consumer attached to the `FormControl`.
 *
 * Must be called from an injection context (a field initializer or constructor).
 */
export function controlStateSignal(
  control: Signal<AbstractControl | null | undefined>,
): Signal<ControlState> {
  // `control.events` re-emits on every change; `startWith` forces one synchronous read so the
  // state is correct as soon as a control is present, and `switchMap` re-subscribes if it swaps.
  const events = toSignal(
    toObservable(control).pipe(switchMap((c) => (c ? c.events.pipe(startWith(null)) : of(null)))),
    { initialValue: null },
  );

  return computed<ControlState>(() => {
    events();

    const c = control();
    if (!c) {
      return EMPTY_STATE;
    }

    return {
      invalid: c.invalid,
      touched: c.touched,
      dirty: c.dirty,
      pending: c.pending,
      errors: c.errors,
      showError: c.invalid && c.touched,
    };
  });
}
